import type {
  StoredDocument,
  Document,
  DocumentBlock,
  DocumentSection,
  DocumentField,
} from '@pleased-npm/fe-customisation';

/**
 * Parse a StoredDocument by converting stringified blocks to objects
 */
export const parseStoredDocument = (stored: StoredDocument): Document => {
  return {
    ...stored,
    blocks: JSON.parse(stored.blocks),
  };
};

/**
 * Serialize a Document by stringifying blocks for storage
 */
export const serializeDocument = (doc: Document): StoredDocument => {
  return {
    ...doc,
    blocks: JSON.stringify(doc.blocks),
  };
};

/**
 * Find a block by key in a document
 * Uses linear search which is optimal for small arrays (typically 3-10 blocks)
 * @throws Error if block not found
 */
export const findBlockByKey = (document: Document, key: string): DocumentBlock => {
  const block = document.blocks.find((b) => b.key === key);
  if (!block) {
    throw new Error(`Block with key "${key}" not found in document "${document.name}"`);
  }
  return block;
};

/**
 * Find a section by key in a block
 * Uses linear search which is optimal for small arrays (typically 3-8 sections)
 * @throws Error if section not found
 */
export const findSectionByKey = (block: DocumentBlock, key: string): DocumentSection => {
  const section = block.sections.find((s) => s.key === key);
  if (!section) {
    throw new Error(`Section with key "${key}" not found in block "${block.key}"`);
  }
  return section;
};

/**
 * Find a section by key across all blocks in a document
 * Uses linear search with early exit on match
 * @throws Error if section not found
 */
export const findSectionInDocument = (document: Document, sectionKey: string): DocumentSection => {
  for (const block of document.blocks) {
    const section = block.sections.find((s) => s.key === sectionKey);
    if (section) {
      return section;
    }
  }
  throw new Error(`Section with key "${sectionKey}" not found in document "${document.name}"`);
};

/**
 * Find a field by key in a section
 * Uses linear search which is optimal for small arrays (typically 3-6 fields)
 * @throws Error if field not found
 */
export const findFieldByKey = (section: DocumentSection, key: string): DocumentField => {
  const field = section.fields.find((f) => f.key === key);
  if (!field) {
    throw new Error(`Field with key "${key}" not found in section "${section.key}"`);
  }
  return field;
};

/**
 * Find a field by key across all sections in a block
 * Uses linear search with early exit on match
 * @throws Error if field not found
 */
export const findFieldInBlock = (block: DocumentBlock, fieldKey: string): DocumentField => {
  for (const section of block.sections) {
    const field = section.fields.find((f) => f.key === fieldKey);
    if (field) {
      return field;
    }
  }
  throw new Error(`Field with key "${fieldKey}" not found in block "${block.key}"`);
};

/**
 * Find a field by key across all blocks in a document
 * Uses linear search with early exit on match
 * @throws Error if field not found
 */
export const findFieldInDocument = (document: Document, fieldKey: string): DocumentField => {
  for (const block of document.blocks) {
    for (const section of block.sections) {
      const field = section.fields.find((f) => f.key === fieldKey);
      if (field) {
        return field;
      }
    }
  }
  throw new Error(`Field with key "${fieldKey}" not found in document "${document.name}"`);
};

/**
 * Non-throwing variant of findFieldByKey. Returns null when the field is absent.
 * Used for reactive rendering where a field added to the registry after a document
 * was stored must fall back to a default rather than crash the page.
 */
export const findFieldByKeyOrNull = (section: DocumentSection, key: string): DocumentField | null =>
  section.fields.find((f) => f.key === key) ?? null;

/**
 * Non-throwing variant of findFieldInBlock. Returns null when the field is absent.
 */
export const findFieldInBlockOrNull = (block: DocumentBlock, fieldKey: string): DocumentField | null => {
  for (const section of block.sections) {
    const field = section.fields.find((f) => f.key === fieldKey);
    if (field) {
      return field;
    }
  }
  return null;
};

/**
 * Non-throwing variant of findFieldInDocument. Returns null when the field is absent.
 */
export const findFieldInDocumentOrNull = (document: Document, fieldKey: string): DocumentField | null => {
  for (const block of document.blocks) {
    for (const section of block.sections) {
      const field = section.fields.find((f) => f.key === fieldKey);
      if (field) {
        return field;
      }
    }
  }
  return null;
};

/**
 * Create a map of documents by name for O(1) lookup
 */
export const createDocumentMap = (documents: Document[]): Map<string, Document> => {
  return new Map(documents.map((doc) => [doc.registryId, doc]));
};
